/**
 * AICredentialsDialog — lets the signed-in user supply their own OpenAI /
 * Gemini / Anthropic API keys so AI generation calls can use a higher-tier
 * paid account (instead of the shared Emergent Universal Key, which has
 * shared quota / lower model tiers).
 *
 * Storage:
 *   • The keys are PUT to /api/auth/me { ai_keys: {...} } so they persist
 *     server-side, tied to the user account. Backend AI endpoints
 *     automatically read user.ai_keys[provider] before falling back to
 *     the shared Emergent Universal Key.
 *
 * Privacy: the keys are masked (password input) and shown as "•••• abcd"
 * once saved. Users can clear any key with one click. Keys are never
 * mirrored to client-side storage — only the server holds them.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Key, Save, Eye, EyeOff, Trash2, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDERS = [
  {
    id: 'openai',
    label: 'OpenAI · ChatGPT',
    placeholder: 'sk-...',
    docsUrl: 'https://platform.openai.com/api-keys',
    description: 'Use your own GPT-5.2 / GPT Image 1 quota. Keys start with sk-...',
    accent: '#10A37F',
  },
  {
    id: 'gemini',
    label: 'Google · Gemini',
    placeholder: 'AIza...',
    docsUrl: 'https://aistudio.google.com/app/apikey',
    description: 'Use your own Gemini 3 / Nano Banana quota. Keys start with AIza...',
    accent: '#1F4FBE',
  },
  {
    id: 'anthropic',
    label: 'Anthropic · Claude',
    placeholder: 'sk-ant-...',
    docsUrl: 'https://console.anthropic.com/settings/keys',
    description: 'Use your own Claude Sonnet / Opus / Haiku 4.5 quota. Keys start with sk-ant-...',
    accent: '#D97706',
  },
];

function maskKey(k) {
  if (!k) return '';
  if (k.length <= 8) return '•'.repeat(k.length);
  return `••••••••${k.slice(-4)}`;
}

export default function AICredentialsDialog({ open, onClose }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [keys, setKeys] = useState({ openai: '', gemini: '', anthropic: '' });
  const [showFor, setShowFor] = useState({}); // { provider: bool }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverKeys, setServerKeys] = useState({}); // masked snapshot of what's saved

  useEffect(() => {
    if (!open || !adminToken) return;
    setLoading(true);
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(({ data }) => {
        const aiKeys = (data && data.ai_keys) || {};
        setServerKeys(aiKeys);
        setKeys({
          openai: aiKeys.openai || '',
          gemini: aiKeys.gemini || '',
          anthropic: aiKeys.anthropic || '',
        });
      })
      .catch(() => { /* silent — dialog still works for fresh entry */ })
      .finally(() => setLoading(false));
  }, [open, adminToken]);

  const save = async () => {
    if (!adminToken) { toast.error('Sign in to save AI credentials.'); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/auth/me`,
        { ai_keys: keys },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      const aiKeys = (data && data.ai_keys) || {};
      setServerKeys(aiKeys);
      toast.success('AI credentials saved. Higher-tier generation is now active.');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save AI credentials');
    } finally {
      setSaving(false);
    }
  };

  const clearKey = (provider) => {
    setKeys((k) => ({ ...k, [provider]: '' }));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-credentials-dialog" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Key className="w-5 h-5 text-amber-600" />
            AI Credentials · Bring Your Own Keys
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500" style={{ fontFamily: 'Manrope' }}>
            Supply your own OpenAI, Gemini, or Anthropic API keys to unlock higher-tier models, faster generation, and your own paid quota. Keys are encrypted at rest and never shared between users.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {PROVIDERS.map((p) => {
              const value = keys[p.id] || '';
              const saved = serverKeys[p.id] || '';
              const visible = !!showFor[p.id];
              return (
                <div
                  key={p.id}
                  data-testid={`ai-cred-${p.id}-card`}
                  className="p-4 rounded-xl border"
                  style={{ borderColor: `${p.accent}30`, backgroundColor: `${p.accent}08`, fontFamily: 'Manrope' }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="text-sm font-bold" style={{ color: p.accent }}>{p.label}</h3>
                    <a
                      href={p.docsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-[10px] font-bold underline"
                      style={{ color: p.accent }}
                      data-testid={`ai-cred-${p.id}-docs`}
                    >
                      Get a key <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                  <p className="text-[11px] text-gray-600 mb-2">{p.description}</p>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid={`ai-cred-${p.id}-input`}
                      type={visible ? 'text' : 'password'}
                      value={value}
                      onChange={(e) => setKeys((k) => ({ ...k, [p.id]: e.target.value }))}
                      placeholder={p.placeholder}
                      className="flex-1 px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
                      style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowFor((s) => ({ ...s, [p.id]: !s[p.id] }))}
                      className="p-2 rounded-lg hover:bg-white/60"
                      title={visible ? 'Hide key' : 'Show key'}
                      data-testid={`ai-cred-${p.id}-toggle-visibility`}
                    >
                      {visible ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                    </button>
                    {value && (
                      <button
                        type="button"
                        onClick={() => clearKey(p.id)}
                        className="p-2 rounded-lg hover:bg-red-50"
                        title="Clear this key"
                        data-testid={`ai-cred-${p.id}-clear`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    )}
                  </div>
                  {saved && (
                    <p className="text-[10px] mt-1.5" style={{ color: p.accent }}>
                      ✓ Saved · current: <code className="font-mono">{maskKey(saved)}</code>
                    </p>
                  )}
                </div>
              );
            })}
            <div className="rounded-xl p-3 text-[11px] text-gray-600" style={{ backgroundColor: '#F5F0E1', fontFamily: 'Manrope' }}>
              <strong className="text-amber-700">Tip:</strong> You don't need to fill all three. Add whichever provider you have paid quota for. AI calls in the app will automatically prefer your key when set, and fall back to the shared Universal Key when it's not.
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            data-testid="ai-cred-cancel"
            className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100"
            style={{ fontFamily: 'Manrope' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            data-testid="ai-cred-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
            style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save AI keys
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
